/**
 * Service de diagnostic et de santé Firestore
 * Gère la connectivité, les erreurs et les mécanismes de retry
 */

import { db } from './firebase';
import { doc, getDoc, enableNetwork, disableNetwork } from 'firebase/firestore';
import { toast } from 'react-toastify';

export interface FirestoreHealthStatus {
  isConnected: boolean;
  lastError?: string;
  retryCount: number;
  lastSuccessfulConnection?: Date;
}

class FirestoreHealthService {
  private static instance: FirestoreHealthService;
  private healthStatus: FirestoreHealthStatus = {
    isConnected: false,
    retryCount: 0
  };
  private retryTimeout?: NodeJS.Timeout;
  private maxRetries = 5;
  private retryDelay = 2000; // 2 secondes
  private healthCheckInterval?: NodeJS.Timeout;
  private listeners: ((status: FirestoreHealthStatus) => void)[] = [];

  private constructor() {
    this.initializeHealthCheck();
  }

  public static getInstance(): FirestoreHealthService {
    if (!FirestoreHealthService.instance) {
      FirestoreHealthService.instance = new FirestoreHealthService();
    }
    return FirestoreHealthService.instance;
  }

  /**
   * Initialise la surveillance de la santé Firestore
   */
  private async initializeHealthCheck(): Promise<void> {

    
    // Test initial de connectivité
    await this.checkFirestoreHealth();
    
    // Surveillance périodique (toutes les 30 secondes)
    this.healthCheckInterval = setInterval(() => {
      this.checkFirestoreHealth();
    }, 30000);

    // Écouter les erreurs globales
    this.setupGlobalErrorHandling();
  }

  /**
   * Vérifie la santé de Firestore
   */
  public async checkFirestoreHealth(): Promise<boolean> {
    try {

      
      // Test simple de lecture d'une collection accessible publiquement
      const testDocRef = doc(db, 'health-check', 'test');
      await getDoc(testDocRef);
      
      // Connexion réussie
      this.healthStatus = {
        isConnected: true,
        retryCount: 0,
        lastSuccessfulConnection: new Date()
      };
      

      this.notifyListeners();
      return true;
      
    } catch (error: any) {
      this.healthStatus = {
        isConnected: false,
        lastError: error.message || 'Erreur de connexion inconnue',
        retryCount: this.healthStatus.retryCount + 1
      };
      
      this.handleConnectionError(error);
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Gère les erreurs de connexion avec retry automatique
   */
  private handleConnectionError(error: any): void {
    const errorCode = error.code || 'unknown';
    const errorMessage = error.message || 'Erreur inconnue';
    
    // Tentative de reconnexion
    
    // Analyser le type d'erreur
    if (errorCode.includes('unavailable') || errorCode.includes('deadline-exceeded')) {
      // Problème de réseau détecté
    } else if (errorCode.includes('permission-denied')) {
      // Problème de permissions Firestore
      toast.error('Erreur de permissions. Vérifiez votre authentification.');
      return; // Ne pas retry sur les erreurs de permissions
    }
    
    // Retry automatique si sous la limite
    if (this.healthStatus.retryCount < this.maxRetries) {
      const delay = this.retryDelay * Math.pow(2, this.healthStatus.retryCount - 1); // Backoff exponentiel
      
      // Nouvelle tentative programmée
      
      this.retryTimeout = setTimeout(() => {
        this.checkFirestoreHealth();
      }, delay);
    } else {
      toast.error('Problème de connexion persistant. Vérifiez votre connexion internet.');
    }
  }

  /**
   * Configure la gestion globale des erreurs
   */
  private setupGlobalErrorHandling(): void {
    // Écouter les erreurs de réseau
    window.addEventListener('online', () => {
      // Connexion internet rétablie
      this.resetRetryCount();
      this.checkFirestoreHealth();
    });

    window.addEventListener('offline', () => {
      // Connexion internet perdue
      this.healthStatus.isConnected = false;
      this.healthStatus.lastError = 'Connexion internet perdue';
      this.notifyListeners();
    });
  }

  /**
   * Remet à zéro le compteur de retry
   */
  public resetRetryCount(): void {
    this.healthStatus.retryCount = 0;
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }
  }

  /**
   * Force une reconnexion Firestore
   */
  public async forceReconnect(): Promise<boolean> {
    try {
      // Reconnexion forcée à Firestore
      
      // Désactiver puis réactiver le réseau Firestore
      await disableNetwork(db);
      await new Promise(resolve => setTimeout(resolve, 1000));
      await enableNetwork(db);
      
      // Vérifier la connectivité
      return await this.checkFirestoreHealth();
    } catch (error) {
      return false;
    }
  }

  /**
   * Ajoute un listener pour les changements de statut
   */
  public addStatusListener(listener: (status: FirestoreHealthStatus) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Supprime un listener
   */
  public removeStatusListener(listener: (status: FirestoreHealthStatus) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Notifie tous les listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.healthStatus);
      } catch (error) {
        // Erreur dans le listener de statut
      }
    });
  }

  /**
   * Retourne le statut actuel
   */
  public getHealthStatus(): FirestoreHealthStatus {
    return { ...this.healthStatus };
  }

  /**
   * Nettoie les ressources
   */
  public cleanup(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    this.listeners = [];
  }
}

export default FirestoreHealthService.getInstance();